import * as React from 'react';
import moment from 'moment';
import { connectWithRouter } from 'helpers/connectWithRouter';
import { bindActionCreators, Dispatch } from 'redux';
import { hasPermission } from 'helpers/Permission';
import { getOrgTransactions, getMoreOrgTransactions, emptyOrgTransactions, OrgTransactionsParams } from 'actions/Transactions/GetOrgTransactions';
import { editTransaction } from 'actions/Transactions/Edit';
import RecordsTable, { Column, BulkAction } from './RecordsTable';
import EditRecordModal from './EditRecordModal';
import SyncStatusFilterBar from './SyncStatusFilterBar';
import { ReduxState } from 'reducers/types';
import { PermissionTypes } from '../../constants';
import { QUERY_LIMIT_25 } from '../../configs/General';
import CircularProgress from '@mui/material/CircularProgress';
import { Transaction, Cursor, SYNC_STATUS_OPTIONS } from './expenseTypes';

const styles = require('./recordStyles.pcss');

const COLUMNS: Column[] = [
  {
    label: 'Merchant',
    render: (r: Transaction) => r.merchant ?? '',
  },
  {
    label: 'Date',
    render: (r: Transaction) => {
      const raw = r.postingDate ?? r.created ?? '';
      return moment(raw).isValid() ? moment(raw).format('MMM DD, YYYY') : '';
    },
  },
  {
    label: 'Amount',
    render: (r: Transaction) => `$${(r.amount ?? 0).toFixed(2)}`,
  },
  {
    label: 'Last 4',
    render: (r: Transaction) => r.last4 ?? '',
  },
  {
    label: 'Sync Status',
    render: (r: Transaction) => r.syncToGlStatus ?? '',
  },
];

type Props = {
  organizationId: string;
  transactions: Transaction[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  cursor: Cursor | null;
  success: boolean | null;
  getOrgTransactions: (organizationId: string, params?: OrgTransactionsParams) => void;
  getMoreOrgTransactions: (organizationId: string, params: OrgTransactionsParams, cursor: Cursor) => void;
  editTransaction: (params: { transactionIds: string[]; syncToGlStatus: string }) => void;
  emptyOrgTransactions: () => void;
};

type State = {
  editModalOpen: boolean;
  selectedTransaction: Transaction | null;
  filterSyncStatus: string;
  filterDateFrom: string;
  filterDateTo: string;
};

class OrgTransactionsTable extends React.Component<Props, State> {
  private sentinelRef = React.createRef<HTMLDivElement>();
  private observer: IntersectionObserver | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      editModalOpen: false,
      selectedTransaction: null,
      filterSyncStatus: '',
      filterDateFrom: '',
      filterDateTo: '',
    };
  }

  componentDidMount() {
    if (this.props.organizationId) {
      this.fetchTransactions();
    }
    this.setupObserver();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.success !== this.props.success && this.props.success === true) {
      this.fetchTransactions();
      this.setState({ editModalOpen: false, selectedTransaction: null });
    }

    if (prevProps.hasMore !== this.props.hasMore || prevProps.isLoadingMore !== this.props.isLoadingMore) {
      this.setupObserver();
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.props.emptyOrgTransactions();
  }

  setupObserver = () => {
    if (this.observer) {
      this.observer.disconnect();
    }

    const sentinel = this.sentinelRef.current;
    if (!sentinel || !this.props.hasMore || this.props.isLoadingMore) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.loadMore();
        }
      },
      { threshold: 0.1 },
    );

    this.observer.observe(sentinel);
  };

  getFilterParams = (): OrgTransactionsParams => {
    const params: OrgTransactionsParams = { limit: QUERY_LIMIT_25 };
    if (this.state.filterSyncStatus) {
      params.syncToGlStatus = this.state.filterSyncStatus;
    }
    if (this.state.filterDateFrom) {
      params.dateFrom = this.state.filterDateFrom;
    }
    if (this.state.filterDateTo) {
      params.dateTo = this.state.filterDateTo;
    }
    return params;
  };

  fetchTransactions = () => {
    this.props.getOrgTransactions(this.props.organizationId, this.getFilterParams());
  };

  loadMore = () => {
    if (this.props.cursor && this.props.hasMore && !this.props.isLoadingMore) {
      this.props.getMoreOrgTransactions(
        this.props.organizationId,
        this.getFilterParams(),
        this.props.cursor,
      );
    }
  };

  handleFilterApply = () => {
    this.fetchTransactions();
  };

  handleFilterReset = () => {
    this.setState(
      { filterSyncStatus: '', filterDateFrom: '', filterDateTo: '' },
      () => this.fetchTransactions(),
    );
  };

  onClickRow = (transaction: Transaction) => {
    if (hasPermission('expenseReport', PermissionTypes.update)) {
      this.setState({ editModalOpen: true, selectedTransaction: transaction });
    }
  };

  onCloseModal = () => {
    this.setState({ editModalOpen: false, selectedTransaction: null });
  };

  onSubmitEdit = (values: Record<string, string>) => {
    const { selectedTransaction } = this.state;
    if (selectedTransaction?.id) {
      this.props.editTransaction({
        transactionIds: [selectedTransaction.id],
        syncToGlStatus: values.syncToGlStatus,
      });
    }
  };

  getDisplayFields = () => {
    const t = this.state.selectedTransaction;
    return [
      { label: 'Merchant', value: t?.merchant ?? '' },
      { label: 'Last 4', value: t?.last4 ?? '' },
      {
        label: 'Amount',
        value: `$${(t?.amount ?? 0).toFixed(2)}`,
      },
    ];
  };

  getEditFields = () => [
    {
      label: 'Sync to GL Status',
      key: 'syncToGlStatus',
      options: SYNC_STATUS_OPTIONS,
    },
  ];

  getInitialValues = () => ({
    syncToGlStatus: this.state.selectedTransaction?.syncToGlStatus ?? '',
  });

  onBulkUpdateSyncStatus = (selectedIds: string[], status: string) => {
    this.props.editTransaction({
      transactionIds: selectedIds,
      syncToGlStatus: status,
    });
  };

  getBulkActions = (): BulkAction[] => {
    return SYNC_STATUS_OPTIONS.map((opt) => ({
      label: `Set ${opt.label}`,
      onClick: (ids: string[]) => this.onBulkUpdateSyncStatus(ids, opt.value),
    }));
  };

  render() {
    const canEdit = hasPermission('expenseReport', PermissionTypes.update);

    return (
      <>
        <SyncStatusFilterBar
          labelId="transaction-sync-filter-label"
          filterSyncStatus={this.state.filterSyncStatus}
          filterDateFrom={this.state.filterDateFrom}
          filterDateTo={this.state.filterDateTo}
          onSyncStatusChange={(value) => this.setState({ filterSyncStatus: value })}
          onDateFromChange={(value) => this.setState({ filterDateFrom: value })}
          onDateToChange={(value) => this.setState({ filterDateTo: value })}
          onApply={this.handleFilterApply}
          onReset={this.handleFilterReset}
        />

        <RecordsTable
          columns={COLUMNS}
          records={this.props.transactions}
          tabs={[]}
          isLoading={this.props.isLoading}
          onClickRow={canEdit ? this.onClickRow : undefined}
          keyField="id"
          selectable={canEdit}
          bulkActions={canEdit ? this.getBulkActions() : undefined}
        />

        {this.props.hasMore && (
          <div ref={this.sentinelRef} className={styles.sentinelLoader}>
            {this.props.isLoadingMore && <CircularProgress size={24} />}
          </div>
        )}

        <EditRecordModal
          open={this.state.editModalOpen}
          title="Edit Transaction"
          displayFields={this.getDisplayFields()}
          editFields={this.getEditFields()}
          initialValues={this.getInitialValues()}
          onClose={this.onCloseModal}
          onSubmit={this.onSubmitEdit}
        />
      </>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    transactions: state.orgTransactions.transactions,
    isLoading: state.orgTransactions.isLoading,
    isLoadingMore: state.orgTransactions.isLoadingMore,
    hasMore: state.orgTransactions.hasMore,
    cursor: state.orgTransactions.cursor,
    success: state.orgTransactions.success,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      getOrgTransactions,
      getMoreOrgTransactions,
      editTransaction,
      emptyOrgTransactions,
    },
    dispatch,
  );
}

export default connectWithRouter(
  mapStateToProps,
  mapDispatchToProps,
)(OrgTransactionsTable);
